import React, { useState } from 'react';
import { Dumbbell, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const COMPOUND_LIFTS = [
  { value: 'bench-press', label: 'Bench Press' },
  { value: 'back-squat', label: 'Back Squat' },
  { value: 'deadlift', label: 'Deadlift' },
  { value: 'overhead-press', label: 'Overhead Press' },
  { value: 'barbell-row', label: 'Barbell Row' },
  { value: 'front-squat', label: 'Front Squat' },
  { value: 'hip-thrust', label: 'Hip Thrust' },
  { value: 'other', label: 'Other Exercise' },
];

const OneRepMaxCalculator = () => {
  const [exercise, setExercise] = useState('');
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [unit, setUnit] = useState('kg');
  const [result, setResult] = useState(null);

  // Brzycki Formula: 1RM = weight × (36 / (37 - reps))
  const calculateBrzycki = (w, r) => w * (36 / (37 - r));
  
  // Epley Formula: 1RM = weight × (1 + reps / 30)
  const calculateEpley = (w, r) => w * (1 + r / 30);

  const calculateOneRepMax = () => {
    const w = parseFloat(weight);
    const r = parseInt(reps);

    if (!w || !r || r < 1 || r > 12) {
      alert('Please enter valid weight and reps (1-12 reps for accurate results)');
      return;
    }

    if (!exercise) {
      alert('Please select an exercise');
      return;
    }

    // Use average of Brzycki and Epley for more accuracy
    const brzycki = calculateBrzycki(w, r);
    const epley = calculateEpley(w, r);
    const oneRM = (brzycki + epley) / 2;

    // Calculate percentage table
    const percentages = [
      { percent: 100, reps: '1' },
      { percent: 95, reps: '2' },
      { percent: 90, reps: '4' },
      { percent: 85, reps: '6' },
      { percent: 80, reps: '8' },
      { percent: 75, reps: '10' },
      { percent: 70, reps: '12' },
      { percent: 65, reps: '15' },
      { percent: 60, reps: '20' },
    ];

    const table = percentages.map(p => ({
      ...p,
      weight: Math.round(oneRM * (p.percent / 100))
    }));

    const selectedLift = COMPOUND_LIFTS.find(l => l.value === exercise);

    setResult({
      oneRM: Math.round(oneRM),
      table,
      exerciseName: selectedLift ? selectedLift.label : 'Exercise'
    });
  };

  const clearForm = () => {
    setExercise('');
    setWeight('');
    setReps('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#1A1A1A]">
        <div className="container">
          <Link to="/resources" className="inline-flex items-center gap-2 text-[#C9A962] hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back to Resources</span>
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Dumbbell className="text-[#C9A962]" size={32} />
              <span className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase">Fitness Tool</span>
            </div>
            <h1 className="heading-display text-white mb-6">One Rep Max Calculator</h1>
            <p className="text-lg md:text-xl text-white/90">
              Estimate your one-rep max (1RM) based on the weight you can lift for multiple reps. 
              Use this to plan your training loads and track strength progress.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 md:p-12 shadow-sm">
              {/* Exercise Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-3 tracking-wide uppercase" data-testid="exercise-label">Exercise</label>
                <select
                  data-testid="exercise-select"
                  value={exercise}
                  onChange={(e) => setExercise(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors bg-white text-[#1A1A1A] appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B6B6B' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center'
                  }}
                >
                  <option value="">Select a compound lift</option>
                  {COMPOUND_LIFTS.map(lift => (
                    <option key={lift.value} value={lift.value}>{lift.label}</option>
                  ))}
                </select>
                <p className="text-xs text-[#6B6B6B] mt-1">Choose the exercise you performed</p>
              </div>

              {/* Unit Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-3 tracking-wide uppercase">Units</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setUnit('kg')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      unit === 'kg'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Kilograms (kg)
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnit('lbs')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      unit === 'lbs'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Pounds (lbs)
                  </button>
                </div>
              </div>

              {/* Inputs */}
              <div className="space-y-6 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    Weight Lifted ({unit})
                  </label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder={unit === 'kg' ? 'e.g. 100' : 'e.g. 225'}
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">Enter the weight you lifted</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    Reps Performed
                  </label>
                  <input
                    type="number"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                    placeholder="e.g. 5"
                    min="1"
                    max="12"
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">Number of reps completed (1-12 for best accuracy)</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={calculateOneRepMax}
                  className="py-4 bg-[#C9A962] text-[#1A1A1A] text-sm font-semibold tracking-[0.1em] uppercase hover:bg-[#d4b872] transition-colors"
                >
                  Calculate
                </button>
                <button
                  onClick={clearForm}
                  className="py-4 bg-[#F5F0EB] text-[#1A1A1A] text-sm font-semibold tracking-[0.1em] uppercase hover:bg-[#EDE8E3] transition-colors"
                >
                  Clear
                </button>
              </div>

              {/* Result */}
              {result && (
                <div className="bg-[#1A1A1A] p-8 text-center mb-8" data-testid="orm-result">
                  <p className="text-white/60 text-sm font-medium mb-1">{result.exerciseName}</p>
                  <p className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase mb-2">Estimated One Rep Max</p>
                  <p className="text-5xl font-bold text-white mb-2" style={{fontFamily: "'Oswald', sans-serif"}}>
                    {result.oneRM} {unit}
                  </p>
                </div>
              )}

              {/* Percentage Table */}
              {result && (
                <div>
                  <h3 className="heading-small mb-4">Training Load Chart</h3>
                  <p className="text-sm text-[#6B6B6B] mb-6">
                    Use this chart to determine training weights based on your estimated 1RM.
                  </p>
                  <div className="border border-[#E5E0DB]">
                    <div className="grid grid-cols-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-wide uppercase">
                      <div className="p-3 text-center">% of 1RM</div>
                      <div className="p-3 text-center">Weight ({unit})</div>
                      <div className="p-3 text-center">Est. Reps</div>
                    </div>
                    {result.table.map((row) => (
                      <div 
                        key={row.percent} 
                        className={`grid grid-cols-3 text-sm ${
                          row.percent % 20 === 0 ? 'bg-[#F5F0EB]' : 'bg-white'
                        } ${row.percent === 100 ? 'font-semibold text-[#C9A962]' : ''}`}
                      >
                        <div className="p-3 text-center">{row.percent}%</div>
                        <div className="p-3 text-center">{row.weight}</div>
                        <div className="p-3 text-center">{row.reps}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* How to Use */}
            <div className="mt-12 bg-white p-8">
              <h2 className="heading-medium mb-6">How to Use This Calculator</h2>
              <div className="space-y-4 text-sm text-[#6B6B6B]">
                <p>
                  <strong className="text-[#1A1A1A]">1. Test your strength:</strong> Choose a compound lift (squat, bench press, deadlift, etc.) and perform a set with a weight you can lift for 2-10 reps with good form.
                </p>
                <p>
                  <strong className="text-[#1A1A1A]">2. Enter your numbers:</strong> Input the weight you lifted and the number of reps you completed.
                </p>
                <p>
                  <strong className="text-[#1A1A1A]">3. Use the results:</strong> Your estimated 1RM helps you plan training loads. For example, use 70-85% of your 1RM for strength training.
                </p>
              </div>
            </div>

            {/* Training Zones */}
            <div className="mt-8 bg-white p-8">
              <h2 className="heading-medium mb-6">Training Intensity Zones</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-[#E5E0DB]">
                  <div>
                    <span className="font-semibold text-[#1A1A1A]">Strength</span>
                    <p className="text-xs text-[#6B6B6B]">1-5 reps per set</p>
                  </div>
                  <span className="text-[#C9A962] font-semibold">85-100%</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#E5E0DB]">
                  <div>
                    <span className="font-semibold text-[#1A1A1A]">Hypertrophy</span>
                    <p className="text-xs text-[#6B6B6B]">6-12 reps per set</p>
                  </div>
                  <span className="text-[#C9A962] font-semibold">67-85%</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <div>
                    <span className="font-semibold text-[#1A1A1A]">Endurance</span>
                    <p className="text-xs text-[#6B6B6B]">12+ reps per set</p>
                  </div>
                  <span className="text-[#C9A962] font-semibold">50-67%</span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-[#EDE8E3] text-center">
              <p className="text-xs text-[#6B6B6B]">
                This calculator provides an estimate based on the Brzycki and Epley formulas. 
                Actual 1RM may vary. Always prioritise proper form and safety when testing maximal lifts.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OneRepMaxCalculator;
